import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, Check, Pen, RefreshCw, MessageCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  fixMalformedReferences,
  stripImageMarkdown,
} from '@/features/chat/shared';
import {
  ChatMessage,
  ToolCallDisplay,
  InlineSourcesDisplay,
  LoadingDots,
  type SourceFile,
} from '@/features/chat/shared/components';
import { ToolCallStep } from '@/features/chat/shared/hooks/useStreamingChat';
import { useAutoScroll } from '@/features/chat/shared/hooks/useAutoScroll';
import { useMarkdownComponents } from '@/features/chat/shared/hooks/useMarkdownComponents';
import { stripReferences } from '@/features/chat/shared/utils/clipboard';

import { FileType } from './types';

// Screenshot data extracted from tool results
export interface ScreenshotData {
  url: string;
  fileName?: string;
  timestamp?: string;
}

interface ChatMessagesProps {
  completion: string;
  isLoading: boolean;
  stage: string;
  loadingStage: string | null;
  files: {
    data: FileType[];
  };
  hideReferences: boolean;
  resultSearchTerm?: string;
  setResultSearchTerm?: React.Dispatch<React.SetStateAction<string>>;
  onCopyContent?: (content: string) => void;
  copySuccess?: boolean;
  onClearCompletion?: () => void;
  onEditQuery?: (messageIndex: number, newContent: string) => void;
  onRegenerateResponse?: (messageIndex: number) => void;
  conversationHistory?: Array<{
    role: 'user' | 'assistant';
    content: string;
    screenshots?: ScreenshotData[];
  }>;
  hideToolbar?: boolean;
  isFileDragging?: boolean;
  isLoadingFromHistory?: boolean;
  toolCallSteps?: ToolCallStep[];
  isSearchingFiles?: boolean;
  /** Files that were auto-selected by tool calls (shown inline after response) */
  autoSelectedSources?: SourceFile[];
  // Screenshots captured from tool calls for the current streaming response
  pendingScreenshots?: ScreenshotData[];
}

const ChatMessages: React.FC<ChatMessagesProps> = ({
  completion,
  isLoading,
  stage,
  files,
  hideReferences,
  resultSearchTerm: externalResultSearchTerm,
  onEditQuery,
  onRegenerateResponse,
  conversationHistory = [],
  isFileDragging = false,
  isLoadingFromHistory = false,
  toolCallSteps = [],
  isSearchingFiles = false,
  autoSelectedSources = [],
  pendingScreenshots = [],
}) => {
  const [internalResultSearchTerm] = useState('');
  const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(
    null,
  );
  const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(
    null,
  );
  const [showLoadingDots, setShowLoadingDots] = useState(false);

  // Track the last conversation length to detect when a new message is added from streaming
  const previousConversationLengthRef = React.useRef(
    conversationHistory.length,
  );
  const [justAddedMessageIndex, setJustAddedMessageIndex] = useState<
    number | null
  >(null);

  // Track the conversation length when loading from history started
  // Messages that existed when isLoadingFromHistory became true should never animate
  const loadingFromHistoryLengthRef = React.useRef(0);

  // When isLoadingFromHistory becomes true, capture the current conversation length
  // All messages up to this index should never animate
  React.useEffect(() => {
    if (isLoadingFromHistory) {
      loadingFromHistoryLengthRef.current = conversationHistory.length;
    }
  }, [isLoadingFromHistory, conversationHistory.length]);

  // Detect when a new assistant message is added (from streaming completion)
  React.useEffect(() => {
    const currentLength = conversationHistory.length;
    const previousLength = previousConversationLengthRef.current;

    // Check if a new message was added and it's an assistant message
    if (currentLength > previousLength) {
      const newMessageIndex = currentLength - 1;
      const newMessage = conversationHistory[newMessageIndex];

      // Only skip animation if it's an assistant message (which came from streaming)
      if (newMessage?.role === 'assistant') {
        setJustAddedMessageIndex(newMessageIndex);
        // Clear the flag after a short delay to allow future animations
        setTimeout(() => setJustAddedMessageIndex(null), 100);
      }
    }

    previousConversationLengthRef.current = currentLength;
  }, [conversationHistory]);

  // Control loading dots visibility with delay for first message animation
  React.useEffect(() => {
    if (isLoading && stage === 'processing') {
      // Check if this is the first message (conversation has exactly 1 user message)
      const isFirstMessage = conversationHistory.length === 1;

      if (isFirstMessage) {
        // Delay showing loading dots for first message entrance animation (600ms)
        const timer = setTimeout(() => setShowLoadingDots(true), 650);
        return () => clearTimeout(timer);
      }
      // Show immediately for subsequent messages
      setShowLoadingDots(true);
      return undefined;
    }
    // Hide loading dots when not in processing stage
    setShowLoadingDots(false);
    return undefined;
  }, [isLoading, stage, conversationHistory.length]);

  // Use shared auto-scroll hook with ChatGPT-style behavior
  const { containerRef, scrollAnchorRef, lastUserMessageRef } = useAutoScroll({
    dependencies: [conversationHistory.length, completion],
    enabled: true,
    isStreaming: isLoading,
  });

  // Find the index of the last user message for ref attachment
  const lastUserMessageIndex = React.useMemo(() => {
    for (let i = conversationHistory.length - 1; i >= 0; i -= 1) {
      if (conversationHistory[i].role === 'user') {
        return i;
      }
    }
    return -1;
  }, [conversationHistory]);

  // Use external search term if provided, otherwise use internal
  const resultSearchTerm = externalResultSearchTerm ?? internalResultSearchTerm;

  // Use shared markdown components hook with file reference support
  const markdownComponents = useMarkdownComponents({
    files,
    hideReferences,
    searchTerm: resultSearchTerm,
  });

  const isEmpty =
    !completion &&
    !isLoading &&
    stage !== 'processing' &&
    conversationHistory.length === 0;

  return (
    <div className="overflow-hidden flex flex-col">
      <div
        className={cn(
          'flex-1 overflow-y-auto overflow-x-hidden',
          isEmpty ? 'flex items-center justify-center' : 'flex flex-col',
        )}
        role="log"
        ref={containerRef}
      >
        {isEmpty ? (
          <div className="text-center p-8 max-w-md mx-auto">
            {(() => {
              if (isFileDragging) {
                return (
                  <p className="text-sm text-muted-foreground">
                    Drop to add context
                  </p>
                );
              }
              return (
                <div className="space-y-3">
                  <MessageCircle className="w-8 h-8 text-muted-foreground/30 mx-auto" />
                  <p className="text-sm text-muted-foreground">
                    Search across all your recordings
                  </p>
                </div>
              );
            })()}
          </div>
        ) : (
          <>
            <div className="px-3 pt-2">
              <div className="max-w-3xl mx-auto">
                {/* Conversation Messages */}
                <div className="space-y-0 smooth-scroll">
                  {conversationHistory.map((message, index) => {
                    const isEditing = editingMessageIndex === index;
                    // Check if this is the very first user message in the conversation
                    const isFirstUserMessage =
                      index === 0 && message.role === 'user';
                    // Check if this message was just added from streaming (to skip animation)
                    const wasJustAddedFromStream =
                      index === justAddedMessageIndex;

                    // Build actions based on role
                    const actions =
                      message.role === 'user'
                        ? [
                            {
                              icon: Pen,
                              onClick: () => setEditingMessageIndex(index),
                              title: 'Edit message',
                            },
                          ]
                        : [
                            {
                              icon: Copy,
                              onClick: () => {
                                // Strip references before copying
                                const cleanContent = stripReferences(
                                  message.content,
                                );
                                navigator.clipboard.writeText(cleanContent);
                                setCopiedMessageIndex(index);
                                setTimeout(
                                  () => setCopiedMessageIndex(null),
                                  2000,
                                );
                              },
                              title: 'Copy message',
                              activeIcon: Check,
                              isActive: copiedMessageIndex === index,
                              activeColor: 'text-green-600',
                            },
                            {
                              icon: RefreshCw,
                              onClick: () => onRegenerateResponse?.(index),
                              title: 'Regenerate response',
                            },
                          ];

                    // Determine animation class
                    let animationClass = '';
                    // Never animate assistant messages (they come from streaming and should stay in place)
                    // Only animate user messages that weren't loaded from history
                    // Check if this message existed when we started loading from history
                    const wasLoadedFromHistory =
                      index < loadingFromHistoryLengthRef.current;

                    if (
                      message.role === 'user' &&
                      !wasJustAddedFromStream &&
                      !wasLoadedFromHistory
                    ) {
                      if (isFirstUserMessage) {
                        animationClass = 'animate-first-message-entrance';
                      } else {
                        animationClass = 'animate-fade-in-up';
                      }
                    }

                    // Check if this is the last assistant message and we have auto-selected sources
                    const isLastAssistantMessage =
                      message.role === 'assistant' &&
                      index === conversationHistory.length - 1;
                    const showInlineSources =
                      isLastAssistantMessage &&
                      autoSelectedSources.length > 0 &&
                      !isLoading;

                    // Check if this is the last user message (for scroll ref)
                    const isLastUserMessage = index === lastUserMessageIndex;

                    // Process message content based on role
                    // Always strip image markdown for assistant messages because:
                    // 1. If tool was called → screenshots display via screenshots array
                    // 2. If tool wasn't called → LLM may output broken/multiline markdown from memory
                    //    that won't render properly anyway, so strip it for clean output
                    const getMessageContent = () => {
                      if (message.role !== 'assistant') {
                        return message.content;
                      }
                      return stripImageMarkdown(
                        fixMalformedReferences(message.content),
                      );
                    };

                    const chatMessageElement = (
                      <React.Fragment key={index}>
                        <ChatMessage
                          role={message.role}
                          content={getMessageContent()}
                          actions={actions}
                          isEditing={isEditing}
                          onEdit={(newContent) => {
                            onEditQuery?.(index, newContent);
                            setEditingMessageIndex(null);
                          }}
                          onCancelEdit={() => setEditingMessageIndex(null)}
                          markdownComponents={
                            message.role === 'assistant'
                              ? markdownComponents
                              : undefined
                          }
                          searchTerm={resultSearchTerm}
                          className={animationClass}
                          actionsAddon={
                            showInlineSources ? (
                              <InlineSourcesDisplay
                                files={autoSelectedSources}
                              />
                            ) : undefined
                          }
                        />
                        {/* Display screenshots from history for assistant messages */}
                        {message.role === 'assistant' &&
                          message.screenshots &&
                          message.screenshots.length > 0 && (
                            <div className="px-2 pb-2 space-y-2">
                              {message.screenshots.map((screenshot, idx) => (
                                <div
                                  key={idx}
                                  className="rounded-lg overflow-hidden border border-border bg-muted/30 max-w-md"
                                >
                                  <a
                                    href={screenshot.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block"
                                  >
                                    <img
                                      src={screenshot.url}
                                      alt={`Screenshot from ${screenshot.fileName || 'video'}${screenshot.timestamp ? ` at ${screenshot.timestamp}` : ''}`}
                                      className="w-full h-auto object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                      loading="lazy"
                                    />
                                  </a>
                                  {(screenshot.fileName ||
                                    screenshot.timestamp) && (
                                    <a
                                      href={screenshot.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block px-3 py-1.5 text-xs text-muted-foreground bg-muted/50 hover:text-primary hover:underline transition-colors"
                                    >
                                      {screenshot.fileName}
                                      {screenshot.timestamp &&
                                        ` @ ${screenshot.timestamp}`}
                                    </a>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                      </React.Fragment>
                    );

                    // Wrap the last user message with a ref for ChatGPT-style scrolling
                    if (isLastUserMessage) {
                      return (
                        <div key={index} ref={lastUserMessageRef}>
                          {chatMessageElement}
                        </div>
                      );
                    }

                    return chatMessageElement;
                  })}

                  {/* Show tool call display during loading/processing phase */}
                  {showLoadingDots && isLoading && stage === 'processing' && (
                    <div className="px-2 py-2">
                      <ToolCallDisplay
                        thinkingText={
                          isSearchingFiles
                            ? 'Searching your recordings...'
                            : 'Thinking about your request...'
                        }
                        steps={toolCallSteps}
                        isVisible
                        defaultExpanded
                      />
                    </div>
                  )}

                  {/* Thinking dots - shows before streaming starts */}
                  {!completion &&
                    stage === 'final' &&
                    isLoading &&
                    conversationHistory[conversationHistory.length - 1]
                      ?.role !== 'assistant' && (
                      <div className="flex items-start gap-1 py-1 w-full">
                        <div className="px-2 py-2">
                          <LoadingDots dotSize={1.5} />
                        </div>
                      </div>
                    )}

                  {/* Streaming message - shows completion as it streams */}
                  {/* Only show streaming message if it's not already in conversation history */}
                  {completion &&
                    stage === 'final' &&
                    isLoading &&
                    conversationHistory[conversationHistory.length - 1]
                      ?.role !== 'assistant' && (
                      <div className="flex items-start gap-1 py-1 w-full group">
                        <div className="flex flex-col items-start">
                          <div className="relative w-fit mb-0.5 px-2 py-0">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={markdownComponents}
                            >
                              {/* Always strip image markdown - screenshots display via pendingScreenshots array */}
                              {stripImageMarkdown(
                                fixMalformedReferences(completion),
                              )}
                            </ReactMarkdown>
                            {/* Show thinking dots during tool call pause */}
                            {isSearchingFiles && (
                              <div className="mt-2">
                                <LoadingDots dotSize={1.5} />
                              </div>
                            )}
                            {/* Display screenshots captured during this response */}
                            {pendingScreenshots.length > 0 && (
                              <div className="mt-3 space-y-2">
                                {pendingScreenshots.map((screenshot, idx) => (
                                  <div
                                    key={idx}
                                    className="rounded-lg overflow-hidden border border-border bg-muted/30 max-w-md"
                                  >
                                    <a
                                      href={screenshot.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block"
                                    >
                                      <img
                                        src={screenshot.url}
                                        alt={`Screenshot from ${screenshot.fileName || 'video'}${screenshot.timestamp ? ` at ${screenshot.timestamp}` : ''}`}
                                        className="w-full h-auto object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                        loading="lazy"
                                      />
                                    </a>
                                    {(screenshot.fileName ||
                                      screenshot.timestamp) && (
                                      <a
                                        href={screenshot.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block px-3 py-1.5 text-xs text-muted-foreground bg-muted/50 hover:text-primary hover:underline transition-colors"
                                      >
                                        {screenshot.fileName}
                                        {screenshot.timestamp &&
                                          ` @ ${screenshot.timestamp}`}
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          {/* Invisible placeholder for action buttons to prevent layout shift */}
                          <div className="flex items-center gap-1 mt-0.5 ml-2 opacity-0 pointer-events-none">
                            <div className="p-1 w-[28px] h-[28px]" />
                            <div className="p-1 w-[28px] h-[28px]" />
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </div>
            {/* Dynamic spacer for ChatGPT-style scrolling */}
            {/* Appears INSTANTLY when loading starts (no transition) */}
            {/* Shrinks smoothly when streaming completes */}
            <div
              style={{
                minHeight: isLoading ? '70vh' : '0',
                // Only animate when shrinking (going from loading to not loading)
                // Expanding is instant, shrinking is smooth
                transition: isLoading ? 'none' : 'min-height 500ms ease-out',
              }}
              aria-hidden="true"
            />
            <div ref={scrollAnchorRef} />
          </>
        )}
      </div>
    </div>
  );
};

export default ChatMessages;
